<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { getI18n } from '@/plugins/i18n'

const i18n = getI18n()

const props = defineProps({
  label: {
    type: String,
    required: false,
    default: null,
  },
  // List mode: only the first `collapsedCount` items are visible while collapsed
  items: {
    type: Array,
    required: false,
    default: null,
  },
  collapsedCount: {
    type: Number,
    required: false,
    default: 1,
  },
  // Content mode: the default slot is clipped to this height while collapsed
  collapsedHeight: {
    type: String,
    required: false,
    default: null,
  },
  // Expanded content taller than this scrolls instead of growing
  maxHeight: {
    type: String,
    required: false,
    default: '33vh',
  },
  dense: {
    type: Boolean,
    required: false,
    default: true,
  },
  // Always render the toggle (content-mode with lazily-rendered slot that has nothing to overflow)
  alwaysShowToggle: {
    type: Boolean,
    required: false,
    default: false,
  },
  expandLabel: {
    type: String,
    required: false,
    default: null,
  },
  collapseLabel: {
    type: String,
    required: false,
    default: null,
  },
})

const expanded = ref(false)
const renderedOnce = ref(false)
const contentRef = ref(null)
const overflowing = ref(false)
const fullHeight = ref(0)

const toggle = () => {
  expanded.value = !expanded.value
  if (expanded.value) renderedOnce.value = true
}

const headItems = computed(() => props.items.slice(0, props.collapsedCount))
const tailItems = computed(() => props.items.slice(props.collapsedCount))

const hasMore = computed(() => props.alwaysShowToggle
  || (props.items ? props.items.length > props.collapsedCount : overflowing.value))

const toggleLabel = computed(() => (expanded.value
  ? props.collapseLabel || i18n.t('common.showLess')
  : props.expandLabel || i18n.t('common.showMore')))

const contentStyle = computed(() => {
  if (props.items) {
    return expanded.value && props.maxHeight ? { maxHeight: props.maxHeight, overflowY: 'auto' } : null
  }
  if (!props.collapsedHeight) return null
  if (!expanded.value) return { maxHeight: props.collapsedHeight, overflow: 'hidden' }
  return {
    maxHeight: props.maxHeight ? `min(${fullHeight.value}px, ${props.maxHeight})` : `${fullHeight.value}px`,
    overflowY: props.maxHeight ? 'auto' : 'hidden',
  }
})

// `scrollHeight` is the untruncated content height even while clipped, so the expanded
// target height is known before the transition starts
const measureContent = () => {
  if (!contentRef.value) return
  fullHeight.value = contentRef.value.scrollHeight
  if (!expanded.value) overflowing.value = contentRef.value.scrollHeight > contentRef.value.clientHeight + 1
}

let resizeObserver = null

onMounted(() => {
  if (props.items || !props.collapsedHeight) return
  measureContent()
  resizeObserver = new ResizeObserver(measureContent)
  resizeObserver.observe(contentRef.value)
})

onBeforeUnmount(() => resizeObserver?.disconnect())
</script>

<template>
  <div>
    <q-item-label v-if="props.label" caption class="expandable-label">{{ props.label }}</q-item-label>
    <div ref="contentRef" class="expandable-body" :style="contentStyle">
      <template v-if="props.items">
        <template v-for="(item, index) in headItems" :key="item.id ?? index">
          <slot name="item" :item="item" :index="index" />
        </template>
        <q-slide-transition>
          <div v-show="expanded">
            <template v-for="(item, index) in tailItems" :key="item.id ?? index">
              <slot name="item" :item="item" :index="props.collapsedCount + index" />
            </template>
          </div>
        </q-slide-transition>
      </template>
      <template v-else>
        <q-slide-transition v-if="props.alwaysShowToggle">
          <div v-show="expanded">
            <slot v-if="renderedOnce" :expanded="expanded" />
          </div>
        </q-slide-transition>
        <slot v-else :expanded="expanded" />
      </template>
    </div>
    <div v-if="hasMore" class="flex flex-center">
      <q-btn
        flat
        no-caps
        size="sm"
        color="primary"
        :ripple="false"
        :dense="props.dense"
        icon-right="mdi-chevron-down"
        class="expandable-toggle"
        :class="{ 'expandable-toggle--expanded': expanded }"
        :label="toggleLabel"
        @click="toggle"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.expandable-label {
  margin-bottom: 4px;
}

.expandable-body {
  transition: max-height 0.3s ease;
}

.expandable-toggle :deep(.q-icon) {
  transition: transform 0.3s ease;
}

.expandable-toggle--expanded :deep(.q-icon) {
  transform: rotate(180deg);
}
</style>
